import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { EyeIcon, EyeOffIcon, ChevronLeftIcon, ChevronRightIcon, CheckIcon } from 'lucide-react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import {
  formatCPFOrCNPJ,
  formatCEP,
  isValidCPFOrCNPJ,
  isValidCEP,
  getDocumentType
} from '../../utils/documentValidator';

const RegisterPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    cpf_cnpj: '',
    company_name: '',
    cep: '',
    address: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  const { register } = useAuth();
  const navigate = useNavigate();

  // Verificar se é CNPJ
  const isCNPJ = getDocumentType(formData.cpf_cnpj) === 'CNPJ';

  const steps = [
    { id: 1, title: 'Dados Pessoais', description: 'Informações básicas' },
    { id: 2, title: 'Endereço', description: 'Localização' },
    { id: 3, title: 'Senha', description: 'Segurança da conta' },
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let formattedValue = value;

    // Aplicar formatação automática
    if (name === 'cpf_cnpj') {
      formattedValue = formatCPFOrCNPJ(value);
    } else if (name === 'cep') {
      formattedValue = formatCEP(value);
    }

    setFormData({
      ...formData,
      [name]: formattedValue,
    });
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        if (!formData.name.trim()) {
          alert('Nome é obrigatório!');
          return false;
        }
        if (!formData.email.trim()) {
          alert('Email é obrigatório!');
          return false;
        }
        if (!formData.phone.trim()) {
          alert('Telefone é obrigatório!');
          return false;
        }
        if (!formData.cpf_cnpj.trim()) {
          alert('CPF ou CNPJ é obrigatório!');
          return false;
        }
        if (!isValidCPFOrCNPJ(formData.cpf_cnpj)) {
          alert('CPF ou CNPJ inválido!');
          return false;
        }
        if (isCNPJ && !formData.company_name.trim()) {
          alert('Nome da empresa é obrigatório para CNPJ!');
          return false;
        }
        return true;

      case 2:
        if (!formData.cep.trim()) {
          alert('CEP é obrigatório!');
          return false;
        }
        if (!isValidCEP(formData.cep)) {
          alert('CEP inválido!');
          return false;
        }
        if (!formData.address.trim()) {
          alert('Endereço é obrigatório!');
          return false;
        }
        if (!formData.number.trim()) {
          alert('Número é obrigatório!');
          return false;
        }
        if (!formData.neighborhood.trim()) {
          alert('Bairro é obrigatório!');
          return false;
        }
        if (!formData.city.trim()) {
          alert('Cidade é obrigatória!');
          return false;
        }
        if (!formData.state.trim()) {
          alert('Estado é obrigatório!');
          return false;
        }
        return true;

      case 3:
        if (!formData.password.trim()) {
          alert('Senha é obrigatória!');
          return false;
        }
        if (formData.password.length < 6) {
          alert('A senha deve ter pelo menos 6 caracteres!');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          alert('As senhas não coincidem!');
          return false;
        }
        return true;

      default:
        return false;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCompletedSteps(prev => [...prev.filter(s => s !== currentStep), currentStep]);
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar o último step antes de enviar
    if (!validateStep(3)) {
      return;
    }

    setIsLoading(true);

    try {
      const success = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        cpf_cnpj: formData.cpf_cnpj,
        company_name: isCNPJ ? formData.company_name : undefined,
        cep: formData.cep,
        address: formData.address,
        number: formData.number,
        complement: formData.complement,
        neighborhood: formData.neighborhood,
        city: formData.city,
        state: formData.state,
      });

      if (success) {
        setFormData({
          name: '',
          email: '',
          password: '',
          confirmPassword: '',
          phone: '',
          cpf_cnpj: '',
          company_name: '',
          cep: '',
          address: '',
          number: '',
          complement: '',
          neighborhood: '',
          city: '',
          state: '',
        });
        setCompletedSteps([]);
        setCurrentStep(1);
        navigate('/dashboard');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Register Form */}
      <div className="flex-1 flex items-center justify-center bg-white px-4 sm:px-6 lg:px-20 xl:px-24 py-8">
        <div className="mx-auto w-full max-w-md lg:w-[480px]">
          <div>
            <div className="flex items-center justify-center mb-12">
              <div className="flex items-center space-x-8">
                <img
                  src="/logo-wabbycall.svg"
                  alt="WabbyCall"
                  className="h-20 w-auto"
                />
                <span className="text-5xl font-bold" style={{fontFamily: 'Orbitron, sans-serif', color: '#31b4e7'}}>WabbyCall</span>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-8">
              Crie sua conta
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Cadastre-se para começar a usar nossos serviços
            </p>
          </div>

          {/* Step Indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              {steps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                      currentStep > step.id
                        ? 'bg-green-500 text-white'
                        : currentStep === step.id
                        ? 'bg-cyan-500 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {completedSteps.includes(step.id) ? (
                        <CheckIcon className="w-4 h-4" />
                      ) : (
                        step.id
                      )}
                    </div>
                    <span className={`text-xs mt-2 font-medium ${
                      currentStep >= step.id ? 'text-gray-900' : 'text-gray-500'
                    }`}>
                      {step.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-4 transition-colors duration-300 ${
                      currentStep > step.id ? 'bg-green-500' : 'bg-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Step 1: Dados Pessoais */}
            {currentStep === 1 && (
              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <div className="w-3 h-3 bg-cyan-500 rounded-full mr-3"></div>
                  Dados Pessoais
                </h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Nome completo *
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      required
                      className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 text-sm"
                      placeholder="Digite seu nome completo"
                      value={formData.name}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      E-mail *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 text-sm"
                      placeholder="Digite seu e-mail"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Telefone *
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        autoComplete="tel"
                        required
                        className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 text-sm"
                        placeholder="(11) 99999-9999"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </div>

                    <div>
                      <label htmlFor="cpf_cnpj" className="block text-sm font-medium text-gray-700 mb-2">
                        CPF ou CNPJ *
                      </label>
                      <input
                        id="cpf_cnpj"
                        name="cpf_cnpj"
                        type="text"
                        required
                        maxLength={18}
                        className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 text-sm"
                        placeholder="000.000.000-00"
                        value={formData.cpf_cnpj}
                        onChange={handleChange}
                      />
                    </div>

                    {isCNPJ && (
                      <div>
                        <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-2">
                          Nome da Empresa *
                        </label>
                        <input
                          id="company_name"
                          name="company_name"
                          type="text"
                          required
                          className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 text-sm"
                          placeholder="Nome da empresa"
                          value={formData.company_name}
                          onChange={handleChange}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Endereço */}
            {currentStep === 2 && (
              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <div className="w-3 h-3 bg-cyan-500 rounded-full mr-3"></div>
                  Endereço
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="cep" className="block text-sm font-medium text-gray-700 mb-2">
                        CEP *
                      </label>
                      <input
                        id="cep"
                        name="cep"
                        type="text"
                        required
                        maxLength={9}
                        className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 text-sm"
                        placeholder="00000-000"
                        value={formData.cep}
                        onChange={handleChange}
                      />
                    </div>

                    <div>
                      <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                        Estado *
                      </label>
                      <select
                        id="state"
                        name="state"
                        required
                        className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 text-sm bg-white"
                        value={formData.state}
                        onChange={handleChange}
                      >
                        <option value="">Selecione um estado</option>
                        <option value="AC">Acre (AC)</option>
                        <option value="AL">Alagoas (AL)</option>
                        <option value="AP">Amapá (AP)</option>
                        <option value="AM">Amazonas (AM)</option>
                        <option value="BA">Bahia (BA)</option>
                        <option value="CE">Ceará (CE)</option>
                        <option value="DF">Distrito Federal (DF)</option>
                        <option value="ES">Espírito Santo (ES)</option>
                        <option value="GO">Goiás (GO)</option>
                        <option value="MA">Maranhão (MA)</option>
                        <option value="MT">Mato Grosso (MT)</option>
                        <option value="MS">Mato Grosso do Sul (MS)</option>
                        <option value="MG">Minas Gerais (MG)</option>
                        <option value="PA">Pará (PA)</option>
                        <option value="PB">Paraíba (PB)</option>
                        <option value="PR">Paraná (PR)</option>
                        <option value="PE">Pernambuco (PE)</option>
                        <option value="PI">Piauí (PI)</option>
                        <option value="RJ">Rio de Janeiro (RJ)</option>
                        <option value="RN">Rio Grande do Norte (RN)</option>
                        <option value="RS">Rio Grande do Sul (RS)</option>
                        <option value="RO">Rondônia (RO)</option>
                        <option value="RR">Roraima (RR)</option>
                        <option value="SC">Santa Catarina (SC)</option>
                        <option value="SP">São Paulo (SP)</option>
                        <option value="SE">Sergipe (SE)</option>
                        <option value="TO">Tocantins (TO)</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                        Logradouro *
                      </label>
                      <input
                        id="address"
                        name="address"
                        type="text"
                        required
                        className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 text-sm"
                        placeholder="Rua, Avenida, etc."
                        value={formData.address}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <label htmlFor="number" className="block text-sm font-medium text-gray-700 mb-2">
                        Número *
                      </label>
                      <input
                        id="number"
                        name="number"
                        type="text"
                        required
                        className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 text-sm"
                        placeholder="123"
                        value={formData.number}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="complement" className="block text-sm font-medium text-gray-700 mb-2">
                        Complemento
                      </label>
                      <input
                        id="complement"
                        name="complement"
                        type="text"
                        className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 text-sm"
                        placeholder="Apto, sala, etc."
                        value={formData.complement}
                        onChange={handleChange}
                      />
                    </div>

                    <div>
                      <label htmlFor="neighborhood" className="block text-sm font-medium text-gray-700 mb-2">
                        Bairro *
                      </label>
                      <input
                        id="neighborhood"
                        name="neighborhood"
                        type="text"
                        required
                        className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 text-sm"
                        placeholder="Nome do bairro"
                        value={formData.neighborhood}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                      Cidade *
                    </label>
                    <input
                      id="city"
                      name="city"
                      type="text"
                      required
                      className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 text-sm"
                      placeholder="Nome da cidade"
                      value={formData.city}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Senha */}
            {currentStep === 3 && (
              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <div className="w-3 h-3 bg-cyan-500 rounded-full mr-3"></div>
                  Senha
                </h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                      Senha *
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        required
                        className="appearance-none block w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 text-sm"
                        placeholder="Mínimo 6 caracteres"
                        value={formData.password}
                        onChange={handleChange}
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        className="absolute inset-y-0 right-0 pr-4 flex items-center hover:text-cyan-600 transition-colors"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOffIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      Confirmar senha *
                    </label>
                    <div className="relative">
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        required
                        className="appearance-none block w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 text-sm"
                        placeholder="Confirme sua senha"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        className="absolute inset-y-0 right-0 pr-4 flex items-center hover:text-cyan-600 transition-colors"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? (
                          <EyeOffIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center pt-4">
              <button
                type="button"
                onClick={prevStep}
                disabled={currentStep === 1}
                className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                <ChevronLeftIcon className="w-4 h-4 mr-2" />
                Anterior
              </button>

              <div className="text-sm text-gray-500">
                {currentStep} de {steps.length}
              </div>

              {currentStep < steps.length ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all duration-200"
                >
                  Próximo
                  <ChevronRightIcon className="w-4 h-4 ml-2" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex items-center px-6 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Criando conta...</span>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <CheckIcon className="w-4 h-4 mr-2" />
                      Criar Conta
                    </div>
                  )}
                </button>
              )}
            </div>

            <div className="text-center pt-4 pb-8">
              <p className="text-sm text-gray-600">
                Já tem uma conta?{' '}
                <Link
                  to="/login"
                  className="font-medium text-cyan-600 hover:text-cyan-500 transition-colors"
                >
                  Faça login
                </Link>
              </p>
            </div>

            <div className="text-center pb-8">
              <p className="text-xs text-gray-500">
                Ao continuar, você concorda com nossos{' '}
                <button type="button" className="text-cyan-600 hover:text-cyan-500 transition-colors underline">
                  termos de serviço
                </button>{' '}
                e{' '}
                <button type="button" className="text-cyan-600 hover:text-cyan-500 transition-colors underline">
                  política de privacidade
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* Right side - Welcome Message with Gradient Background */}
      <div className="hidden lg:block relative w-0 flex-1">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 via-cyan-500 to-blue-600">
          {/* Decorative elements */}
          <div className="absolute top-8 right-8">
            <div className="grid grid-cols-6 gap-2">
              {[...Array(18)].map((_, i) => (
                <div key={i} className="w-2 h-2 bg-white bg-opacity-30 rounded-full"></div>
              ))}
            </div>
          </div>

          <div className="absolute top-1/4 left-8">
            <div className="w-16 h-16 border-4 border-white border-opacity-30 rounded-full"></div>
          </div>

          <div className="absolute bottom-1/4 right-16">
            <div className="w-24 h-24 bg-white bg-opacity-10 rounded-full"></div>
          </div>

          {/* Curved decorative lines */}
          <svg className="absolute top-0 left-0 w-full h-full" viewBox="0 0 400 600" fill="none">
            <path
              d="M-50 200C100 150 200 250 350 200"
              stroke="rgba(255,255,255,0.2)"
              strokeWidth="2"
              fill="none"
            />
            <path
              d="M-50 350C100 300 200 400 350 350"
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="2"
              fill="none"
            />
          </svg>

          {/* Main content */}
          <div className="relative z-10 h-full min-h-full flex items-center justify-center px-8">
            <div className="text-center text-white">
              <div className="mb-8">
                <div className="w-20 h-20 mx-auto mb-4 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                    <div className="w-6 h-6 bg-cyan-500 rounded-full"></div>
                  </div>
                </div>
              </div>
              <h1 className="text-4xl font-bold mb-4">
                Junte-se ao<br />
                WabbyCall
              </h1>
              <p className="text-lg opacity-90">
                Crie sua conta e comece a usar<br />
                nossos serviços de telefonia
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;